import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, DialogContent, TextField, Typography } from '@mui/material';
import { format as formatDate, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { SubscriptionResponse } from '@kimuno/api-sdk/lib/types/responses/Billing';

import WithdrawalIcon from '../../../assets/subscription/withdrawal-plan.svg';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import useSubscriptionInfoData from '../../../utils/useSubscriptionInfoData';
import {
  dateFormat,
  dateTimeFormat,
  manageSubscriptionSteps,
} from '../../../constants';
import { DialogStep } from '../ManageSubscriptionDialog';
import { updateExperiment as updateExperimentAction } from '../../../actions/user.actions';
import useAction from '../../../utils/useAction';
import services from '../../../services';
import { User } from '../../../types/User';
import DialogActions from '../../common/DialogActions';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';

interface WithdrawSubscriptionDialogContentProps {
  user: User;
  subscription: SubscriptionResponse;
  setStep: (step: DialogStep) => void;
  onClose: () => void;
}

const WithdrawSubscriptionDialogContent: React.FC<WithdrawSubscriptionDialogContentProps> = (props) => {
  const { t } = useTranslation();
  const { user, subscription, setStep, onClose } = props;
  const { showNotification } = useContext(NotificationContext);
  const { isXsScreen, isSmScreen } = useScreenSize();
  const updateExperiment = useAction(updateExperimentAction);
  const { productName, startDate } = useSubscriptionInfoData();

  const getSuspensionStartDate = () => {
    const nextChargeDate = subscription.paymentPeriod.nextChargeDate;
    if (subscription.paymentPeriod.type === 'fullAmount' || !nextChargeDate) {
      return new Date();
    }
    return parseISO(nextChargeDate);
  };

  const handleWithdrawalRequest = async () => {
    const submittedAt = Date.now();
    const submissionDateTime = formatDate(new Date(submittedAt), dateTimeFormat, { locale: de });

    try {
      // Mirrors the pause-request flow so support routing and downstream automation
      // treat it as a suspension; the dedicated experiment name (WIDERRUF) marks this as a withdrawal.
      await updateExperiment({
        name: 'WIDERRUF',
        vwoId: 6,
        variation: '2',
        data: {
          isNotVwo: true,
          suspension: {
            status: 'requested',
            date: submittedAt,
          },
        },
      });

      await services.notification.sendSupportNotification({
        template: 'pausing',
        data: {
          email: user.email,
          userId: user.id,
          date: formatDate(new Date(submittedAt), dateFormat),
          type: 'suspension',
          startDate: formatDate(getSuspensionStartDate(), dateFormat),
          status: 'revocation'
        },
      });

      // SM-401. Also record the revocation in kimUno and trigger the AWS SES
      // support-team notification email. Best-effort: a 404 (user has no
      // Postgres customers row — e.g. legacy Zuora), 409 (duplicate open /
      // ineligible), or 502 (backend outage) must not disrupt the success
      // toast the customer already sees from the legacy flow above.
      try {
        await services.billing.submitRevocation({});
      } catch (revocationError) {
        notifyBugsnagHandledError(revocationError as Error);
      }

      showNotification({
        message: t('Your withdrawal was submitted'),
        description: t(
          'We received your withdrawal on {{submissionDateTime}}. You will receive a confirmation by email.',
          { submissionDateTime },
        ),
        severity: 'success',
      });
      onClose();
    } catch (error) {
      showNotification({
        message: t('Your withdrawal was not successful'),
        severity: 'error',
      });
      notifyBugsnagHandledError(error as Error);
    }
  };

  return (
    <>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isXsScreen || isSmScreen ? 'column' : 'row',
            alignItems: 'flex-start',
            flex: '1 0 0',
            alignSelf: 'stretch',
          }}
        >
          {!isXsScreen && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
                '& svg': {
                  width: '400px',
                  height: '300px',
                },
              }}
            >
              <WithdrawalIcon />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: spaces.lg,
              padding: spaces.lg,
              flex: '1 0 0',
              alignSelf: 'stretch',
              alignItems: 'flex-start',
            }}
          >
            <Typography variant='body2' sx={{ color: palette.text.secondary }}>
              {t(
                'Here you can declare the withdrawal of your contract concluded online.' +
                ' After submission, you will receive a confirmation by email.',
              )}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spaces.md,
                alignSelf: 'stretch',
              }}
            >
              <TextField
                disabled
                label={t('Email address')}
                value={user.email ?? ''}
                fullWidth
                slotProps={{
                  input: { readOnly: true },
                  htmlInput: { 'data-testid': 'withdrawal-email' },
                }}
              />
              <TextField
                disabled
                label={t('Subscription / Contract')}
                value={productName}
                fullWidth
                slotProps={{
                  input: { readOnly: true },
                  htmlInput: { 'data-testid': 'withdrawal-product' },
                }}
              />
              <TextField
                disabled
                label={t('Start date')}
                value={startDate}
                fullWidth
                slotProps={{
                  input: { readOnly: true },
                  htmlInput: { 'data-testid': 'withdrawal-start-date' },
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        onBack={() => setStep(manageSubscriptionSteps.manage)}
        primaryButton={{
          text: t('Confirm withdrawal'),
          color: 'primary',
          variant: 'contained',
          onClick: handleWithdrawalRequest,
        }}
        secondaryButton={{
          text: t('Cancel'),
          onClick: onClose,
        }}
      />
    </>
  );
};

export default WithdrawSubscriptionDialogContent;
